import { useDeferredValue, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useModal } from '@/components/modals'
import { useFraudUsers } from '@/lib/api/fraud-queries'
import { cn } from '@/lib/utils'
import { DecisionChip, ScoreBadge, SignalList, Spinner, UserLink } from './shared'
import { ROW, TD, TH, moneyFormatter, relativeTime } from './format'

const PAGE_SIZE = 50
const SCORE_FILTERS = [
  { value: 0, label: 'Everyone' },
  { value: 40, label: 'Score ≥ 40 (step-up band)' },
  { value: 70, label: 'Score ≥ 70' },
  { value: 90, label: 'Score ≥ 90 (decline band)' },
]

export function UsersTab({ eventId, currency }: { eventId: number; currency?: string | null }) {
  const [minScore, setMinScore] = useState(0)
  const [offset, setOffset] = useState(0)
  const [q, setQ] = useState('')
  const deferredQ = useDeferredValue(q)
  const { data, isLoading, error } = useFraudUsers(eventId, { limit: PAGE_SIZE, offset, min_score: minScore, q: deferredQ.trim() || undefined })
  const { openUserModal } = useModal()
  const money = moneyFormatter(currency)
  const rows = data?.results ?? []
  const total = data?.count ?? 0

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setOffset(0) }}
            placeholder="Search name, email, phone or id"
            className="w-72"
          />
          <select
            value={minScore}
            onChange={(e) => { setMinScore(Number(e.target.value)); setOffset(0) }}
            className="rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-1.5 text-sm"
          >
            {SCORE_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          {total.toLocaleString()} attendee{total === 1 ? '' : 's'} with card activity{deferredQ.trim() ? ' matching' : ''} · {PAGE_SIZE} per page · blocked attendees are always listed
        </p>
      </div>

      <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 overflow-x-auto">
        {error ? (
          <p className="p-6 text-sm text-red-600 dark:text-red-400">Failed to load attendees: {error.message}</p>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-16"><Spinner /></div>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-neutral-500 dark:text-neutral-400">No attendees match.</p>
        ) : (
          <table className="w-full whitespace-nowrap">
            <thead>
              <tr className="border-b border-neutral-200 dark:border-neutral-800">
                <th className={TH}>Attendee</th>
                <th className={TH}>Score</th>
                <th className={TH}>Next charge</th>
                <th className={TH}>Reloads</th>
                <th className={TH}>Cards (30d)</th>
                <th className={TH}>Transfers out</th>
                <th className={TH}>Declines / CBs</th>
                <th className={TH}>Account</th>
                <th className={TH}>Signals</th>
                <th className={TH} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.user.id} className={ROW}>
                  <td className={TD}>
                    <UserLink eventId={eventId} user={row.user} />
                    <div className="text-xs text-neutral-500 dark:text-neutral-400">{row.user.email}</div>
                    {row.block && <span className="text-xs text-red-600 dark:text-red-400">{row.block.scope} block · {row.block.reason}</span>}
                  </td>
                  <td className={TD}><ScoreBadge score={row.score} /></td>
                  <td className={TD}>
                    <DecisionChip decision={row.decision} />
                    {row.trusted && <div className="mt-1 text-xs text-green-700 dark:text-green-400">trusted</div>}
                  </td>
                  <td className={TD}>
                    {row.reloads.count} · {money(row.reloads.amount)}
                    <div className="text-xs text-neutral-500 dark:text-neutral-400">
                      {row.reloads.tokens.toLocaleString()} tokens · last {relativeTime(row.reloads.last_at)}
                    </div>
                  </td>
                  <td className={TD}>
                    {row.cards.distinct}
                    <div className="text-xs text-neutral-500 dark:text-neutral-400">
                      {row.cards.all_time != null && row.cards.all_time !== row.cards.distinct ? `${row.cards.all_time} all time · ` : ''}
                      {row.cards.added_24h ? `${row.cards.added_24h} new · ` : ''}
                      {row.cards.shared_with_users ? <span className={cn(row.cards.shared_with_flagged && 'text-red-600 dark:text-red-400')}>shared with {row.cards.shared_with_users}</span> : 'not shared'}
                    </div>
                  </td>
                  <td className={TD}>{row.transfers_out.count} · {row.transfers_out.tokens.toLocaleString()} tokens</td>
                  <td className={TD}>{row.declines_recent} / {row.chargebacks}</td>
                  <td className={TD}>
                    {row.account_age_hours == null ? '—' : row.account_age_hours < 24 ? `${Math.round(row.account_age_hours)}h old` : `${Math.round(row.account_age_hours / 24)}d old`}
                    <div className="text-xs text-neutral-500 dark:text-neutral-400">balance {row.balance.toLocaleString()}</div>
                  </td>
                  <td className={cn(TD, 'whitespace-normal min-w-[14rem]')}><SignalList signals={row.signals} compact /></td>
                  <td className={TD}>
                    <button onClick={() => openUserModal(row.user.id, eventId)} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
                      Profile
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-neutral-600 dark:text-neutral-400">
          <span>{offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
              <ChevronLeft className="w-4 h-4" /> Previous
            </Button>
            <Button variant="outline" size="sm" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>
              Next <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
